import React, { useState, useEffect, useMemo } from 'react';
import { FaEye, FaEdit, FaTrash, FaCheckCircle, FaTimesCircle, FaSearch, FaDownload, FaStore, FaStar, FaBox, FaTags, FaSpinner, FaPhone, FaEnvelope, FaUser, FaLock, FaIdCard, FaCog, FaCopy, FaChevronDown, FaChevronUp, FaMapMarkerAlt, FaFileAlt, FaUniversity, FaHandshake } from 'react-icons/fa';
import { productService, userService, getDocumentUrl } from '../../../../services/apiService';
import AdminLocationDropdown from '../components/AdminLocationDropdown';
import { getDisplayRole, getProfileDisplayEmail, getProfileDisplayName } from '../../../../utils/displayName';
import { toast } from 'react-toastify';

const isDocumentUrl = (v) => {
  if (v == null) return false;
  if (typeof v === 'string') return /^\/uploads\//.test(v) || /^uploads\//.test(v) || /^https?:\/\//.test(v);
  if (typeof v === 'object' && v !== null && typeof v.path === 'string') return true;
  return false;
};
const getDocumentValue = (v) => (v == null ? '' : typeof v === 'object' && v !== null && v.path != null ? v.path : String(v));

const DataRow = ({ label, value, icon: Icon, copyable, onCopy }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (value == null || value === '') return;
    const str = Array.isArray(value) ? value.join(', ') : String(value);
    if (str === '••••••••') return;
    navigator.clipboard?.writeText(str).then(() => {
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), 1500);
    });
  };
  const documentUrl = getDocumentValue(value);
  const isDoc = isDocumentUrl(value);
  const display = value == null ? '—' : Array.isArray(value) ? (value.length ? value.join(', ') : '—') : value === true ? 'Yes' : value === false ? 'No' : isDoc ? 'View document' : String(value);
  const showCopy = copyable && display && display !== '—' && display !== '••••••••' && !isDoc;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0 group">
      {Icon && <div className="mt-0.5 w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 shrink-0 group-hover:bg-gray-200 transition-colors"><Icon className="text-sm" /></div>}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
        {isDoc && documentUrl ? (
          <a href={getDocumentUrl(documentUrl)} target="_blank" rel="noopener noreferrer" className="mt-0.5 text-sm font-medium text-sky-600 hover:underline break-words inline-flex items-center gap-1">
            <FaFileAlt className="text-xs shrink-0" /> View document
          </a>
        ) : (
          <p className="mt-0.5 text-sm font-medium text-gray-900 break-words">{display}</p>
        )}
      </div>
      {showCopy && (
        <button type="button" onClick={handleCopy} className="mt-0.5 p-2 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all shrink-0" title="Copy">
          <FaCopy className={`text-xs ${copied ? 'text-emerald-500' : ''}`} />
        </button>
      )}
    </div>
  );
};

const Vendors = ({ topVendors }) => {
  const [activeTab, setActiveTab] = useState('vendors'); // 'vendors' or 'products'
  const [view, setView] = useState('list'); // 'list', 'edit', 'view', 'productDetail'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState(() => {
    try { return localStorage.getItem('adminLocation') || 'all'; } catch (e) { return 'all'; }
  });
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [expandedAccount, setExpandedAccount] = useState(true);
  const [expandedBusiness, setExpandedBusiness] = useState(true);
  const [expandedAddress, setExpandedAddress] = useState(true);
  const [expandedCatalog, setExpandedCatalog] = useState(true);
  const [expandedKYC, setExpandedKYC] = useState(true);
  const [expandedBank, setExpandedBank] = useState(true);
  const [expandedAgreement, setExpandedAgreement] = useState(true);

  const [formData, setFormData] = useState({
    name: '', email: '', status: 'active', commission: '', registrationDate: ''
  });

  // Fetch vendors
  useEffect(() => {
    fetchVendors();
  }, []);

  // Fetch pending products count on mount and when tab changes
  useEffect(() => {
    fetchPendingProducts();
  }, []);

  // Fetch pending products when products tab is active
  useEffect(() => {
    if (activeTab === 'products') {
      fetchPendingProducts();
    }
  }, [activeTab]);

  // Auto-refresh pending products every 10 seconds (to show updated count)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchPendingProducts(); // Always fetch to update count
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, []);

  // Expand sections when opening a vendor profile
  useEffect(() => {
    if (view === 'view' && selectedVendor) {
      setExpandedAccount(true);
      setExpandedBusiness(true);
      setExpandedAddress(true);
      setExpandedCatalog(true);
      setExpandedKYC(true);
      setExpandedBank(true);
      setExpandedAgreement(true);
    }
  }, [view, selectedVendor?.id]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers();
      const users = response.data || response.users || response;
      const vendorUsers = Array.isArray(users) ? users.filter(user => user.role === 'vendor').map(user => ({
        id: user._id || user.id,
        name: user.vendorDetails?.storeName || user.vendorDetails?.businessName || getProfileDisplayName(user) || '',
        email: getProfileDisplayEmail(user) || '',
        phone: user.phone || '',
        phoneCode: user.phoneCode || '+91',
        role: user.role,
        kycStatus: user.kycStatus || user.vendorDetails?.kycStatus || 'Pending',
        orders: 0,
        revenue: 'N/A',
        rating: null,
        status: user.isActive ? 'active' : 'inactive',
        commission: 'N/A',
        registrationDate: user.createdAt ? new Date(user.createdAt).toISOString().split('T')[0] : 'N/A',
        vendorDetails: user.vendorDetails
      })) : [];
      
      setVendors(vendorUsers);
    } catch (error) {
      toast.error('Failed to fetch vendors');
      console.error('Error fetching vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingProducts = async () => {
    try {
      setLoading(true);
      const response = await productService.getAdminProducts({ approved: false });
      // Handle different response structures
      // Backend returns: { success: true, data: { products: [...], pagination: {...} } }
      // apiService returns: response.data.data which is { products: [...], pagination: {...} }
      let products = [];
      if (response && typeof response === 'object') {
        if (Array.isArray(response)) {
          products = response;
        } else if (response.products && Array.isArray(response.products)) {
          products = response.products;
        } else if (response.data && response.data.products && Array.isArray(response.data.products)) {
          products = response.data.products;
        } else if (response.data && Array.isArray(response.data)) {
          products = response.data;
        }
      }
      setPendingProducts(products);
      console.log('Pending products fetched:', products.length);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch pending products');
      console.error('Error fetching pending products:', error);
      setPendingProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (type, vendor) => {
    setSelectedVendor(vendor);
    if (type === 'edit') {
      setFormData({ ...vendor, password: '' });
      setView('edit');
    } else if (type === 'view') {
      setView('view');
    } else if (type === 'delete') {
      if (window.confirm(`Are you sure you want to permanently delete vendor "${vendor.name}"? This action cannot be undone.`)) {
        try {
          setLoading(true);
          await userService.deleteUser(vendor.id);
          toast.success('Vendor deleted successfully');
          fetchVendors(); // Refresh the list
        } catch (error) {
          console.error('Failed to delete vendor:', error);
          toast.error(error.message || 'Failed to delete vendor');
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    alert(`Vendor "${formData.name}" updated successfully!`);
    setView('list');
  };

  const handleProductAction = async (type, product) => {
    if (type === 'view') {
      setSelectedProduct(product);
      setView('productDetail');
    } else if (type === 'approve') {
      try {
        setLoading(true);
        await productService.approveProduct(product._id || product.id);
        toast.success('Product approved successfully! It will now appear on user page.');
        await fetchPendingProducts();
      } catch (error) {
        toast.error(error.message || 'Failed to approve product');
      } finally {
        setLoading(false);
      }
    } else if (type === 'delete') {
      if (window.confirm(`Are you sure you want to delete/reject product "${product.name}"?`)) {
        try {
          setLoading(true);
          await productService.rejectProduct(product._id || product.id);
          toast.success('Product rejected and deleted successfully!');
          await fetchPendingProducts();
        } catch (error) {
          toast.error(error.message || 'Failed to reject product');
        } finally {
          setLoading(false);
        }
      }
    }
  };

  // Build registered address from parts when single string not set (onboarding saves area/city/state/country/pincode)
  const getRegisteredAddressDisplay = (vd) => {
    if (!vd) return '';
    const single = (vd.registeredAddress || '').trim();
    if (single) return single;
    const parts = [
      vd.registeredArea,
      vd.registeredCity,
      vd.registeredState,
      vd.registeredCountry,
      vd.registeredPincode
    ].filter(Boolean).map((s) => (s || '').toString().trim()).filter(Boolean);
    return parts.length ? parts.join(', ') : '';
  };

  const getVendorLocation = (v) => {
    const vd = v?.vendorDetails || {};
    const addr = getRegisteredAddressDisplay(vd).trim();
    if (addr) return addr;
    const cities = vd.operatingCities;
    if (Array.isArray(cities) && cities.length > 0) return (cities[0] || '').trim();
    return '';
  };

  const uniqueLocations = [...new Set(vendors.map(v => getVendorLocation(v)).filter(Boolean))].sort();

  const filteredVendors = useMemo(() => {
    if (!vendors || !Array.isArray(vendors)) return [];
    return vendors.filter(vendor => {
      if (!vendor) return false;
      const matchesSearch = !searchTerm ||
        (vendor.name && vendor.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (vendor.email && vendor.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (vendor.phone && vendor.phone.includes(searchTerm)) ||
        (getVendorLocation(vendor) && getVendorLocation(vendor).toLowerCase().includes(searchTerm.toLowerCase()));
      const statusNorm = (vendor.status || '').toString().trim().toLowerCase();
      const filterNorm = statusFilter.toString().trim().toLowerCase();
      const matchesStatus = statusFilter === 'all' || filterNorm === statusNorm;
      const matchesLocation = locationFilter === 'all' || getVendorLocation(vendor) === locationFilter;
      return matchesSearch && matchesStatus && matchesLocation;
    });
  }, [vendors, searchTerm, statusFilter, locationFilter]);

  const handleKycStatusChange = async (vendor, newKycStatus) => {
    const id = vendor.id || vendor._id;
    try {
      await userService.updateUser(id, { kycStatus: newKycStatus });
      setVendors(prev => prev.map(v => v.id === id || v._id === id ? { ...v, kycStatus: newKycStatus, vendorDetails: { ...(v.vendorDetails || {}), kycStatus: newKycStatus } } : v));
      toast.success('KYC status updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update KYC status');
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setLocationFilter('all');
    toast.info('Filters cleared');
  };

  const filteredProducts = useMemo(() => {
    if (!pendingProducts || !Array.isArray(pendingProducts)) return [];
    return pendingProducts.filter(product => {
      if (!product) return false;
      const searchLower = productSearchTerm ? productSearchTerm.toLowerCase() : '';
      const matchesSearch = !productSearchTerm ||
        (product.name && product.name.toLowerCase().includes(searchLower)) ||
        (product.sku && product.sku.toLowerCase().includes(searchLower)) ||
        (product.partNumber && product.partNumber.toLowerCase().includes(searchLower));
      return matchesSearch;
    });
  }, [pendingProducts, productSearchTerm]);

  // Export vendors data to CSV
  const handleExport = () => {
    try {
      setExporting(true);
      
      // Determine what to export based on active tab
      let dataToExport = [];
      let fileName = '';
      let csvContent = '';
      
      if (activeTab === 'vendors') {
        // Export filtered vendors or all vendors
        dataToExport = filteredVendors.length > 0 ? filteredVendors : vendors;
        fileName = `vendors_report_${new Date().toISOString().split('T')[0]}.csv`;
        
        // Generate CSV content for vendors
        csvContent = 'Vendor Management Report\n';
        csvContent += `Generated: ${new Date().toLocaleString()}\n`;
        csvContent += `Total Vendors: ${dataToExport.length}\n\n`;
        
        // CSV Headers
        csvContent += 'Vendor Name,Email,Orders,Revenue,Rating,Commission,Status,Registration Date\n';
        
        // CSV Data
        dataToExport.forEach(vendor => {
          const name = (vendor.name || '').replace(/,/g, ';');
          const email = (vendor.email || '').replace(/,/g, ';');
          const orders = vendor.orders || 0;
          const revenue = (vendor.revenue || 'N/A').replace(/,/g, '');
          const rating = vendor.rating || 'N/A';
          const commission = (vendor.commission || 'N/A').replace(/,/g, '');
          const status = (vendor.status || 'N/A').replace(/,/g, ';');
          const regDate = vendor.registrationDate || 'N/A';
          
          csvContent += `${name},${email},${orders},${revenue},${rating},${commission},${status},${regDate}\n`;
        });
      } else if (activeTab === 'products') {
        // Export filtered products or all pending products
        dataToExport = filteredProducts.length > 0 ? filteredProducts : pendingProducts;
        fileName = `pending_products_report_${new Date().toISOString().split('T')[0]}.csv`;
        
        // Generate CSV content for products
        csvContent = 'Pending Products Report\n';
        csvContent += `Generated: ${new Date().toLocaleString()}\n`;
        csvContent += `Total Pending Products: ${dataToExport.length}\n\n`;
        
        // CSV Headers
        csvContent += 'Product Name,SKU,Part Number,Vendor,Price,Stock,Category,Brand,Status\n';
        
        // CSV Data
        dataToExport.forEach(product => {
          const name = (product.name || '').replace(/,/g, ';');
          const sku = (product.sku || 'N/A').replace(/,/g, ';');
          const partNumber = (product.partNumber || 'N/A').replace(/,/g, ';');
          const vendor = (product.vendorId?.name || product.vendorId?.vendorDetails?.storeName || 'N/A').replace(/,/g, ';');
          const price = product.price || 0;
          const stock = product.stock || 0;
          const category = (product.category || 'N/A').replace(/,/g, ';');
          const brand = (product.brand || 'N/A').replace(/,/g, ';');
          const status = (product.status || 'pending').replace(/,/g, ';');
          
          csvContent += `${name},${sku},${partNumber},${vendor},₹${price},${stock},${category},${brand},${status}\n`;
        });
      }
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${dataToExport.length} ${activeTab === 'vendors' ? 'vendors' : 'products'} successfully!`);
    } catch (error) {
      console.error('Failed to export data:', error);
      toast.error('Failed to export data: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  // Product Detail View
  if (view === 'productDetail' && selectedProduct) {
    return (
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-black text-gray-900 uppercase">Product Details</h2>
          <button onClick={() => { setView('list'); setSelectedProduct(null); }} className="text-gray-500 font-bold hover:text-gray-900">← Back</button>
        </div>
        <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Product Images */}
            <div>
              {selectedProduct.images && selectedProduct.images.length > 0 ? (
                <div className="space-y-4">
                  <img loading="lazy" src={selectedProduct.images[0]} alt={selectedProduct.name} className="w-full h-64 object-cover rounded-lg" />
                  {selectedProduct.images.length > 1 && (
                    <div className="grid grid-cols-4 gap-2">
                      {selectedProduct.images.slice(1, 5).map((img, idx) => (
                        <img loading="lazy" key={idx} src={img} alt={`${selectedProduct.name} ${idx + 2}`} className="w-full h-20 object-cover rounded" />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                  <FaBox className="text-6xl text-gray-400" />
                </div>
              )}
            </div>
            
            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <h3 className="text-3xl font-black text-gray-900 mb-2">{selectedProduct.name}</h3>
                <p className="text-gray-500 font-bold">SKU: {selectedProduct.sku || 'N/A'}</p>
                <p className="text-gray-500 font-bold">Part Number: {selectedProduct.partNumber || 'N/A'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <p className="text-[10px] font-black text-gray-400 uppercase">Price</p>
                  <p className="font-bold text-gray-900">₹{selectedProduct.price?.toLocaleString() || 'N/A'}</p>
                  {selectedProduct.mrp && selectedProduct.mrp > selectedProduct.price && (
                    <p className="text-xs text-gray-500 line-through">MRP: ₹{selectedProduct.mrp.toLocaleString()}</p>
                  )}
                </div>
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <p className="text-[10px] font-black text-gray-400 uppercase">Stock</p>
                  <p className="font-bold text-gray-900">{selectedProduct.stock || 0} units</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <p className="text-[10px] font-black text-gray-400 uppercase">Category</p>
                  <p className="font-bold text-gray-900">{selectedProduct.category || 'N/A'}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <p className="text-[10px] font-black text-gray-400 uppercase">Brand</p>
                  <p className="font-bold text-gray-900">{selectedProduct.brand || 'N/A'}</p>
                </div>
              </div>

              {selectedProduct.description && (
                <div>
                  <p className="text-sm font-bold text-gray-400 uppercase mb-2">Description</p>
                  <p className="text-gray-700">{selectedProduct.description}</p>
                </div>
              )}

              {selectedProduct.vehicleCompatibility && selectedProduct.vehicleCompatibility.length > 0 && (
                <div>
                  <p className="text-sm font-bold text-gray-400 uppercase mb-2">Vehicle Compatibility</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedProduct.vehicleCompatibility.map((vehicle, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-bold">
                        {vehicle}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => handleProductAction('approve', selectedProduct)}
                  className="flex-1 py-3 bg-green-600 text-white font-black rounded-xl uppercase tracking-widest hover:bg-green-700 transition shadow-lg"
                  disabled={loading}
                >
                  <FaCheckCircle className="inline mr-2" /> Approve Product
                </button>
                <button
                  onClick={() => handleProductAction('delete', selectedProduct)}
                  className="flex-1 py-3 bg-red-600 text-white font-black rounded-xl uppercase tracking-widest hover:bg-red-700 transition shadow-lg"
                  disabled={loading}
                >
                  <FaTimesCircle className="inline mr-2" /> Reject/Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getProfileImage = (v) => (v?.logo || v?.avatar || v?.profilePhoto || v?.image || v?.vendorDetails?.logo || v?.vendorDetails?.storeLogo || v?.vendorDetails?.profilePhoto || '');

  const renderDetails = (obj, expandNested = false) => {
    if (!obj || typeof obj !== 'object') return null;
    const entries = Object.entries(obj).filter(([k]) => k !== 'password' && k !== 'passwordHash');
    if (entries.length === 0) return null;
    const format = (v) => {
      if (v === undefined || v === null) return '';
      if (v === true) return 'Yes';
      if (v === false) return 'No';
      if (Array.isArray(v)) return v.length ? v.join(', ') : '';
      if (typeof v === 'object' && !expandNested) return JSON.stringify(v);
      return String(v);
    };
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {entries.map(([k, v]) => (
          <div key={k} className="p-4 bg-gray-50 rounded-2xl">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{String(k).replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()}</p>
            {expandNested && typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length > 0 ? (
              <div className="mt-2 space-y-2">{renderDetails(v, true)}</div>
            ) : (
              <p className="font-bold text-gray-900 break-words mt-1">{format(v)}</p>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (view === 'view' && selectedVendor) {
    const vd = selectedVendor.vendorDetails || {};
    const displayName = vd.storeName || vd.businessName || selectedVendor.name;
    const displayEmail = getProfileDisplayEmail(selectedVendor);
    const fullPhone = (selectedVendor.phoneCode || '') + (selectedVendor.phone || '');
    const handleContact = () => {
      if (displayEmail) window.location.href = `mailto:${displayEmail}`;
      else if (fullPhone) window.location.href = `tel:${fullPhone.replace(/\s/g, '')}`;
      else toast.info('No contact details available');
    };
    const CollapseSection = ({ open, onToggle, icon: Icon, title, subtitle, children }) => (
      <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-shadow hover:shadow-md">
        <button type="button" onClick={onToggle} className="w-full px-6 py-4 border-b border-gray-100 bg-gray-50/80 flex items-center justify-between gap-2 text-left hover:bg-gray-100/80 transition-colors">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="text-gray-500" />}
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          </div>
          <span className="text-gray-400">{open ? <FaChevronUp /> : <FaChevronDown />}</span>
        </button>
        {open && <div className="px-6 py-4">{children}</div>}
      </section>
    );

    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">Vendor Profile</h1>
          <button onClick={() => setView('list')} className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors">← Back</button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-shadow hover:shadow-md">
          <div className="p-6 sm:p-8 flex flex-col sm:flex-row gap-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl flex items-center justify-center text-white shrink-0 overflow-hidden bg-gradient-to-br from-emerald-600 to-emerald-700 ring-2 ring-white ring-offset-2 shadow-lg">
              {getProfileImage(selectedVendor) ? (
                <img loading="lazy" src={getProfileImage(selectedVendor)} alt={displayName} className="w-full h-full object-cover" />
              ) : (
                <FaStore className="w-10 h-10 sm:w-12 sm:h-12 text-emerald-200" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold text-gray-900 truncate">{displayName}</h2>
              {displayEmail ? (
                <a href={`mailto:${displayEmail}`} className="mt-1 block text-sm text-slate-600 hover:text-slate-800 hover:underline truncate transition-colors">{displayEmail}</a>
              ) : null}
              {(selectedVendor.phone || selectedVendor.phoneCode) && (
                <a href={`tel:${fullPhone.replace(/\s/g, '')}`} className="mt-0.5 inline-block text-sm text-slate-600 hover:text-slate-800 hover:underline transition-colors">
                  {(selectedVendor.phoneCode || '') + ' ' + (selectedVendor.phone || '')}
                </a>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700">{selectedVendor.role || 'vendor'}</span>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${(selectedVendor.status || '').toLowerCase() === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                  {selectedVendor.status ?? '—'}
                </span>
                {selectedVendor.registrationDate && <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">Joined {selectedVendor.registrationDate}</span>}
              </div>
            </div>
          </div>
        </div>

        <CollapseSection open={expandedAccount} onToggle={() => setExpandedAccount((e) => !e)} icon={FaIdCard} title="Account Information" subtitle="Base user and login details">
          <div className="divide-y divide-gray-100">
            <DataRow label="Name" value={getProfileDisplayName(selectedVendor) || selectedVendor.name || (vd.storeName || vd.businessName) || ''} icon={FaUser} copyable onCopy={() => toast.success('Name copied')} />
            <DataRow label="Email" value={displayEmail || '—'} icon={FaEnvelope} copyable onCopy={() => toast.success('Email copied')} />
            <DataRow label="Password" value="••••••••" icon={FaLock} />
            <DataRow label="Phone" value={selectedVendor.phone || '—'} icon={FaPhone} copyable onCopy={() => toast.success('Phone copied')} />
            <DataRow label="Phone code" value={selectedVendor.phoneCode || (selectedVendor.phone ? '+91' : '') || '—'} icon={FaPhone} copyable onCopy={() => toast.success('Phone code copied')} />
            <DataRow label="Role" value={getDisplayRole(selectedVendor) || selectedVendor.role || '—'} icon={FaCog} copyable onCopy={() => toast.success('Role copied')} />
          </div>
        </CollapseSection>

        <CollapseSection open={expandedBusiness} onToggle={() => setExpandedBusiness((e) => !e)} icon={FaStore} title="Business">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
            <div className="space-y-0">
              <DataRow label="Business name" value={vd.businessName} icon={FaStore} copyable onCopy={() => toast.success('Copied')} />
              <DataRow label="Store name" value={vd.storeName} icon={FaStore} copyable onCopy={() => toast.success('Copied')} />
              <DataRow label="Business type" value={vd.businessType} icon={FaStore} copyable onCopy={() => toast.success('Copied')} />
              <DataRow label="Authorized person" value={vd.authorizedPerson} icon={FaUser} copyable onCopy={() => toast.success('Copied')} />
              <DataRow label="Owner mobile" value={vd.ownerMobile} icon={FaPhone} copyable onCopy={() => toast.success('Copied')} />
              <DataRow label="GST number" value={vd.gstNumber} icon={FaStore} copyable onCopy={() => toast.success('Copied')} />
              <DataRow label="PAN number" value={vd.panNumber} icon={FaStore} copyable onCopy={() => toast.success('Copied')} />
              <DataRow label="Establishment year" value={vd.establishmentYear} icon={FaStore} copyable onCopy={() => toast.success('Copied')} />
            </div>
            <div className="space-y-0 sm:pl-6">
              <DataRow label="Business description" value={vd.businessDescription} icon={FaStore} copyable onCopy={() => toast.success('Copied')} />
              <DataRow label="Business logo" value={vd.businessLogo || null} icon={FaStore} />
            </div>
          </div>
        </CollapseSection>

        <CollapseSection open={expandedAddress} onToggle={() => setExpandedAddress((e) => !e)} icon={FaMapMarkerAlt} title="Address">
          <div className="divide-y divide-gray-100">
            <DataRow label="Registered address" value={getRegisteredAddressDisplay(vd)} icon={FaMapMarkerAlt} copyable onCopy={() => toast.success('Copied')} />
            <DataRow label="Warehouse same as registered" value={vd.warehouseSame} icon={FaMapMarkerAlt} />
            <DataRow label="Operating cities" value={Array.isArray(vd.operatingCities) ? vd.operatingCities : vd.operatingCities} icon={FaMapMarkerAlt} copyable onCopy={() => toast.success('Copied')} />
          </div>
        </CollapseSection>

        <CollapseSection open={expandedCatalog} onToggle={() => setExpandedCatalog((e) => !e)} icon={FaTags} title="Catalog">
          <div className="divide-y divide-gray-100">
            <DataRow label="Categories" value={Array.isArray(vd.categories) ? vd.categories : vd.categories} icon={FaTags} copyable onCopy={() => toast.success('Copied')} />
            <DataRow label="Min order value" value={vd.minOrderValue} icon={FaTags} copyable onCopy={() => toast.success('Copied')} />
            <DataRow label="Same day dispatch" value={vd.sameDayDispatch} icon={FaTags} />
            <DataRow label="Return policy" value={vd.returnPolicy} icon={FaTags} copyable onCopy={() => toast.success('Copied')} />
            <DataRow label="Return window (days)" value={vd.returnWindow} icon={FaTags} copyable onCopy={() => toast.success('Copied')} />
          </div>
        </CollapseSection>

        <CollapseSection open={expandedKYC} onToggle={() => setExpandedKYC((e) => !e)} icon={FaFileAlt} title="KYC">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            <div className="space-y-0 divide-y divide-gray-100">
              <DataRow label="Aadhaar front" value={vd.aadhaarFront || null} icon={FaFileAlt} />
              <DataRow label="Aadhaar back" value={vd.aadhaarBack || null} icon={FaFileAlt} />
              <DataRow label="PAN upload" value={vd.panUpload || null} icon={FaFileAlt} />
            </div>
            <div className="space-y-0 sm:pl-6 divide-y divide-gray-100">
              <DataRow label="GST certificate" value={vd.gstCert || null} icon={FaFileAlt} />
              <DataRow label="Shop license" value={vd.shopLicense || null} icon={FaFileAlt} />
              <DataRow label="Address proof" value={vd.addressProof || null} icon={FaFileAlt} />
            </div>
          </div>
        </CollapseSection>

        <CollapseSection open={expandedBank} onToggle={() => setExpandedBank((e) => !e)} icon={FaUniversity} title="Bank">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            <div className="space-y-0 divide-y divide-gray-100">
              <DataRow label="Bank name" value={vd.bankName} icon={FaUniversity} copyable onCopy={() => toast.success('Copied')} />
              <DataRow label="Account holder name" value={vd.accountHolderName} icon={FaUser} copyable onCopy={() => toast.success('Copied')} />
              <DataRow label="Account number" value={vd.accountNumber} icon={FaUniversity} copyable onCopy={() => toast.success('Copied')} />
              <DataRow label="Confirm account number" value={vd.confirmAccountNumber} icon={FaUniversity} copyable onCopy={() => toast.success('Copied')} />
            </div>
            <div className="space-y-0 sm:pl-6 divide-y divide-gray-100">
              <DataRow label="IFSC code" value={vd.ifscCode} icon={FaUniversity} copyable onCopy={() => toast.success('Copied')} />
              <DataRow label="Account type" value={vd.accountType} icon={FaUniversity} copyable onCopy={() => toast.success('Copied')} />
              <DataRow label="UPI ID" value={vd.upiId} icon={FaUniversity} copyable onCopy={() => toast.success('Copied')} />
              <DataRow label="Cheque upload" value={vd.chequeUpload || null} icon={FaFileAlt} />
            </div>
          </div>
        </CollapseSection>

        <CollapseSection open={expandedAgreement} onToggle={() => setExpandedAgreement((e) => !e)} icon={FaHandshake} title="Agreement">
          <div className="divide-y divide-gray-100">
            <DataRow label="Agreement signed" value={vd.agreementSigned} icon={FaHandshake} />
            <DataRow label="Declaration" value={vd.declaration} icon={FaHandshake} />
            <DataRow label="Enable store" value={vd.enableStore} icon={FaStore} />
          </div>
        </CollapseSection>

        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={() => handleAction('edit', selectedVendor)} className="flex-1 py-3 px-4 bg-emerald-700 text-white text-sm font-semibold rounded-lg hover:bg-emerald-800 hover:scale-[1.01] active:scale-[0.99] transition-all shadow-sm">
            Edit vendor
          </button>
          <button onClick={handleContact} className="flex-1 py-3 px-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 active:scale-[0.99] transition-all">
            Message vendor
          </button>
        </div>
      </div>
    );
  }

  if (view === 'edit') {
    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">Edit Vendor</h1>
          <button type="button" onClick={() => setView('list')} className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors">← Back</button>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          <section className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-shadow hover:shadow-md">
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/80">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <FaStore className="text-gray-500" /> Account & vendor details
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">Update name, email, contact, commission and status</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Vendor name *</label>
                  <input required type="text" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Vendor or store name" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email address *</label>
                  <input required type="email" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="vendor@example.com" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Phone</label>
                  <input type="text" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" value={formData.phone || ''} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 1234567890" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Commission (%)</label>
                  <input type="text" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" value={formData.commission || ''} onChange={e => setFormData({ ...formData, commission: e.target.value })} placeholder="e.g., 10" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Status</label>
                  <select className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white" value={formData.status || 'active'} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Password (leave empty to keep existing)</label>
                  <input type="password" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition" value={formData.password || ''} onChange={e => setFormData({ ...formData, password: e.target.value })} placeholder="Leave empty to keep existing password" />
                  <p className="text-xs text-gray-500 mt-1">Only enter a new password if you want to change it.</p>
                </div>
              </div>
            </div>
          </section>

          <div className="flex flex-col sm:flex-row gap-3">
            <button type="submit" className="flex-1 py-3 px-4 bg-emerald-700 text-white text-sm font-semibold rounded-lg hover:bg-emerald-800 hover:scale-[1.01] active:scale-[0.99] transition-all shadow-sm flex items-center justify-center gap-2">
              Update vendor
            </button>
            <button type="button" onClick={() => setView('list')} className="flex-1 py-3 px-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 active:scale-[0.99] transition-all">
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 tracking-tight">Vendor Management</h1>
          <p className="text-sm text-gray-500 mt-0.5">Approve and manage vendor registrations and products</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleExport}
            disabled={exporting || (activeTab === 'vendors' && filteredVendors.length === 0 && vendors.length === 0) || (activeTab === 'products' && filteredProducts.length === 0 && pendingProducts.length === 0)}
            className="bg-white border border-emerald-500 text-emerald-600 rounded-lg px-4 py-2 flex items-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-emerald-50 transition-colors"
            title={`Export ${activeTab === 'vendors' ? 'vendors' : 'pending products'} to CSV`}
          >
            {exporting ? <FaSpinner className="animate-spin text-emerald-600" /> : <FaDownload className="text-emerald-600" />} <span>Export</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => { setActiveTab('vendors'); setView('list'); }}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'vendors'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaStore className="inline mr-2" /> Vendors
            </button>
            <button
              onClick={() => { setActiveTab('products'); setView('list'); }}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'products'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FaBox className="inline mr-2" /> Pending Products ({pendingProducts.length})
            </button>
          </nav>
        </div>
      </div>

      {/* Pending Products Tab */}
      {activeTab === 'products' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-4 border border-gray-100">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search products by name, SKU, or part number..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="bg-white rounded-lg shadow-md border border-gray-100 p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading products...</p>
            </div>
          )}

          {/* Products Table */}
          {!loading && (
            <div className="bg-white rounded-lg shadow-md border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredProducts.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                          No pending products found.
                        </td>
                      </tr>
                    ) : (
                      filteredProducts.map((product) => (
                        <tr key={product._id || product.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              {product.images && product.images.length > 0 ? (
                                <img loading="lazy" src={product.images[0]} alt={product.name} className="w-12 h-12 object-cover rounded" />
                              ) : (
                                <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
                                  <FaBox className="text-gray-400" />
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-semibold text-gray-900">{product.name}</p>
                                <p className="text-xs text-gray-500">{product.brand || 'N/A'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {product.vendorId?.name || product.vendorId?.vendorDetails?.storeName || 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">{product.sku || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm font-semibold text-gray-900">₹{product.price?.toLocaleString() || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{product.stock || 0}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{product.category || 'N/A'}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleProductAction('view', product)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                title="View Details"
                              >
                                <FaEye />
                              </button>
                              <button
                                onClick={() => handleProductAction('approve', product)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                                title="Approve Product"
                                disabled={loading}
                              >
                                <FaCheckCircle />
                              </button>
                              <button
                                onClick={() => handleProductAction('delete', product)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                title="Reject/Delete"
                                disabled={loading}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vendors Tab */}
      {activeTab === 'vendors' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="relative lg:col-span-2">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  placeholder="Search by name, email, location or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="dashboard-input w-full pl-10 pr-4 py-2.5 text-sm bg-white text-slate-900 placeholder:text-slate-400"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="dashboard-input px-4 py-2.5 text-sm bg-white text-slate-900"
              >
                <option value="all">All status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
              </select>
              <AdminLocationDropdown
                value={locationFilter === 'all' ? '' : locationFilter}
                onChange={(v) => {
                  const val = v || 'all';
                  setLocationFilter(val);
                  try {
                    if (val === 'all') {
                      localStorage.removeItem('adminLocation');
                    } else if (typeof v === 'object') {
                      localStorage.setItem('adminLocation', JSON.stringify(v));
                    } else {
                      localStorage.setItem('adminLocation', String(v));
                    }
                  } catch (e) {}
                }}
                className="w-full"
              />
              <button
                type="button"
                onClick={handleClearFilters}
                disabled={!searchTerm && statusFilter === 'all' && locationFilter === 'all'}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                <FaTimesCircle /> Clear filters
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Showing {filteredVendors.length} of {vendors.length} vendor{vendors.length !== 1 ? 's' : ''}
            </p>
          </div>

          {loading && (
            <div className="flex justify-center items-center py-12 bg-white rounded-xl border border-gray-200">
              <FaSpinner className="animate-spin text-emerald-600 text-3xl" />
            </div>
          )}

          {!loading && filteredVendors.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <FaStore className="mx-auto text-gray-300 text-4xl mb-3" />
              <p className="text-gray-600 font-medium">No vendors found</p>
              <p className="text-sm text-gray-500 mt-1">
                {vendors.length === 0 ? 'No vendors in the system yet.' : 'Try adjusting search or filters.'}
              </p>
              {(searchTerm || statusFilter !== 'all' || locationFilter !== 'all') && (
                <button type="button" onClick={handleClearFilters} className="mt-4 text-sm font-medium text-emerald-600 hover:text-emerald-700">
                  Clear filters
                </button>
              )}
            </div>
          )}

          {!loading && filteredVendors.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Vendor</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Orders</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Revenue</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Rating</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Commission</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">KYC Status</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredVendors.map((vendor) => (
                      <tr key={vendor.id} className="hover:bg-gray-50/50 transition">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center text-white shadow-sm shrink-0">
                              <FaStore className="text-sm" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-900 block truncate">
                                {vendor.vendorDetails?.storeName || vendor.vendorDetails?.businessName || getProfileDisplayName(vendor) || (vendor.name && !/^User\s*\d+$/i.test(vendor.name) ? vendor.name : '') || ''}
                              </p>
                              <p className="text-xs text-gray-500">{getProfileDisplayEmail(vendor) || ''}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">{vendor.orders}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">{vendor.revenue}</td>
                        <td className="px-6 py-4">
                          {vendor.rating ? (
                            <div className="flex items-center gap-1">
                              <FaStar className="text-amber-500 text-xs" />
                              <span className="text-sm font-medium">{vendor.rating}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">{vendor.commission}</td>
                        <td className="px-6 py-4">
                          <select
                            value={vendor.kycStatus || 'Pending'}
                            onChange={(e) => handleKycStatusChange(vendor, e.target.value)}
                            className={`text-xs font-medium rounded-lg px-2.5 py-1.5 border cursor-pointer ${(vendor.kycStatus || 'Pending') === 'Verified' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : (vendor.kycStatus || 'Pending') === 'Rejected' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}
                          >
                            <option value="Pending">Pending</option>
                            <option value="Verified">Verified</option>
                            <option value="Rejected">Rejected</option>
                          </select>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${(vendor.status || '').toLowerCase() === 'active' ? 'bg-emerald-100 text-emerald-800' : (vendor.status || '').toLowerCase() === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                            {vendor.status || '—'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => handleAction('view', vendor)} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition" title="View"><FaEye /></button>
                            <button onClick={() => handleAction('edit', vendor)} className="p-2 text-slate-600 hover:bg-slate-50 rounded-lg transition" title="Edit"><FaEdit /></button>
                            <button onClick={() => handleAction('delete', vendor)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50" title="Delete"><FaTrash /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Vendors;

